import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft, Save, Loader2, Plus, Trash2, CreditCard } from "lucide-react";

/**
 * Mandate — port of Kinntegra-Old `client-mandate`.
 * Row fields lifted from Angular template:
 *   row.Amount, row.ClientKycBankId, row.MandateTypeId
 *
 * ClientKycBankId dropdown is populated from the banks captured during the
 * KYC Information stage for each family member (kyc[member].banks).
 */
const MANDATE_TYPES = ["NACH", "e-Mandate", "SIP Mandate", "One-Time"];

const blankRow = () => ({
  id: crypto.randomUUID?.() || String(Date.now() + Math.random()),
  member_id: "", bank_id: "", amount: "", mandate_type: "NACH",
});

export default function LeadMandatePage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([blankRow()]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/lead-mgmt/${leadId}`);
        setLead(data.data);
        const saved = data.data?.mandates;
        if (Array.isArray(saved) && saved.length) setRows(saved.map(r => ({ ...blankRow(), ...r })));
      } catch (e) {
        toast.error("Failed to load lead: " + (e?.response?.data?.detail || e.message));
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [leadId]);

  const members = (lead?.introduction?.members || []).map(m => ({
    id: m.member_id || m.name, name: m.name,
  }));
  const banksFor = (memberId) => {
    if (!memberId) return [];
    const kyc = (lead?.kyc || {})[memberId] || {};
    return (kyc.banks || []).filter(b => b.bank_name).map(b => ({
      id: b.id, label: `${b.bank_name}${b.account_number ? ` — ${b.account_number.slice(-4).padStart(b.account_number.length, "•")}` : ""}`,
    }));
  };

  const upd = (id, patch) => setRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  const addRow = () => setRows(prev => [...prev, blankRow()]);
  const removeRow = (id) => setRows(prev => prev.length > 1 ? prev.filter(r => r.id !== id) : prev);

  const save = async () => {
    for (const r of rows) {
      if (!r.member_id) return toast.error("Select a family member for each mandate");
      if (!r.bank_id) return toast.error("Select a bank for each mandate");
      if (!(parseFloat(r.amount) > 0)) return toast.error("Enter a valid amount for each mandate");
    }
    setBusy(true);
    try {
      await api.post(`/lead-mgmt/${lead.id}/mandate`, { mandates: rows });
      toast.success("Mandates saved");
      navigate(`/leads/${leadId}/uploads`);
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-teal-600" size={32}/></div>;
  if (!lead) return <div className="p-10 text-center text-gray-400">Lead not found.</div>;

  const inp = "w-full min-w-0 h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500";
  const sel = "w-full min-w-0 h-9 px-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500";

  return (
    <div className="px-4 py-6" data-testid="lead-mandate-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/leads/${leadId}/account-opening`)}
                  className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 hover:bg-gray-100"
                  data-testid="mandate-back-btn"><ArrowLeft size={16}/></button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Mandate</h1>
            <p className="text-xs text-gray-500 mt-0.5">Register debit mandates for SIP / One-Time investments — {lead.first_name} {lead.last_name}</p>
          </div>
        </div>
      </div>

      <Card className="border-gray-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <CreditCard size={15}/> Mandates
            </label>
            <button type="button" onClick={addRow}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-gray-50"
                    data-testid="mandate-add-btn">
              <Plus size={14}/> Add Mandate
            </button>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="hidden lg:grid gap-2 px-4 py-3 bg-gray-50 text-[11px] font-semibold uppercase tracking-wider text-gray-500 border-b"
                 style={{ gridTemplateColumns: '1.5fr 2fr 1fr 1fr 0.5fr' }}>
              <span className="min-w-0">Family Member</span>
              <span className="min-w-0">Bank</span>
              <span className="min-w-0">Amount (INR)</span>
              <span className="min-w-0">Type</span>
              <span className="min-w-0 text-center">Action</span>
            </div>
            <div className="divide-y divide-gray-100">
              {rows.map((r) => (
                <div key={r.id}
                     className="grid grid-cols-1 lg:grid gap-2 px-4 py-3 items-center"
                     style={{ gridTemplateColumns: '1.5fr 2fr 1fr 1fr 0.5fr' }}
                     data-testid={`mandate-row-${r.id}`}>
                  <select value={r.member_id}
                          onChange={e => upd(r.id, { member_id: e.target.value, bank_id: "" })}
                          className={sel} data-testid={`mandate-member-${r.id}`}>
                    <option value="">Select member</option>
                    {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                  <select value={r.bank_id} onChange={e => upd(r.id, { bank_id: e.target.value })}
                          disabled={!r.member_id}
                          className={`${sel} disabled:bg-gray-100`} data-testid={`mandate-bank-${r.id}`}>
                    <option value="">{r.member_id ? "Select bank" : "Pick member first"}</option>
                    {banksFor(r.member_id).map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
                  </select>
                  <input type="number" min="0" value={r.amount}
                         onChange={e => upd(r.id, { amount: e.target.value })}
                         className={inp} data-testid={`mandate-amount-${r.id}`}/>
                  <select value={r.mandate_type} onChange={e => upd(r.id, { mandate_type: e.target.value })}
                          className={sel} data-testid={`mandate-type-${r.id}`}>
                    {MANDATE_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                  <div className="min-w-0 flex justify-center">
                    {rows.length > 1 && (
                      <button onClick={() => removeRow(r.id)}
                              className="text-rose-500 hover:bg-rose-50 rounded-md p-1.5"
                              data-testid={`mandate-remove-${r.id}`}>
                        <Trash2 size={14}/>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end pt-4 border-t mt-6">
            <button onClick={save} disabled={busy}
                    className="inline-flex items-center gap-2 rounded-md bg-[#1a7f6f] hover:bg-[#166a5c] text-white text-sm font-semibold px-8 py-2 disabled:opacity-60"
                    data-testid="mandate-save-btn">
              {busy ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>} Save &amp; Continue to Uploads
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
